import { useQuery, QueryKey, UseQueryResult, useQueryClient, UseQueryOptions } from '@tanstack/react-query'

import { Success, Fail, ExtendedError } from 'store/types'
import { FixMeAny } from 'types/global'
import { FETCHING_STATUS } from 'utils/constants'

export type AppQueryOptions<R = FixMeAny> = UseQueryOptions<R, ExtendedError>

type UseAppQuery = <R>(
  queryKey: QueryKey,
  queryFn: () => Promise<Fail | Success<R>>,
  options?: AppQueryOptions<R>,
) => UseQueryResult<R, ExtendedError>

export const useAppQuery: UseAppQuery = (queryKey, queryFn, options) =>
  useQuery({
    ...options,
    queryKey,
    queryFn: async () => {
      const response = await queryFn()
      if (response.status === FETCHING_STATUS.success) {
        return response.data
      }
      throw response.error
    },
    retry: 1,
  })

type AppQueryMap = <R, M>(
  queryKey: QueryKey,
  queryFn: () => Promise<Fail | Success<R>>,
  mapFn: (original: R) => M,
  options?: UseQueryOptions<any, ExtendedError>,
) => UseQueryResult<M, ExtendedError>

export const useAppQueryMap: AppQueryMap = (queryKey, queryFn, mapFn, options) => {
  const queryWithMap = async () => {
    const response = await queryFn()
    if (response.status === FETCHING_STATUS.success) {
      const mapped = mapFn(response.data)
      return mapped
    }
    throw response.error
  }
  return useQuery({
    ...options,
    queryKey,
    queryFn: queryWithMap,
    retry: 1,
  })
}

export const useQueryData = <R>(queryKeys: QueryKey) => {
  const queryClient = useQueryClient()
  return queryClient.getQueryData<R>(queryKeys)
}

export const useQueryDataMap = <R, M>(queryKeys: QueryKey, mapFn: (unMapped: R) => M) => {
  const queryClient = useQueryClient()
  const queryData = queryClient.getQueryData<R>(queryKeys)
  if (!queryData) {
    return undefined
  }
  return mapFn(queryData) as M
}

export default useAppQuery
